<template>
	<div class="flex flex-col">
		<div :class="['flex flex-col', buttonContainerClass || 'relative']">
			<div ref="buttonEl" class="flex flex-col">
				<slot v-bind="{ isOpen, open, close }"> </slot>
			</div>
			<div
				ref="reference"
				:class="['pointer-events-none absolute h-full w-full', referenceClass]"
				:style="referenceStyles"
			></div>
		</div>
		<div
			v-if="isOpen && !shouldCenterAsDialog"
			class="fixed top-safe right-safe bottom-safe left-safe"
			:style="{ zIndex: zIndex || 4 }"
			@click.stop="close"
		></div>
		<Teleport v-if="body" to="body">
			<div
				v-if="isOpen && shouldCenterAsDialog"
				class="fixed bg-black/50 top-safe right-safe bottom-safe left-safe"
				:style="{ zIndex: zIndex || 4 }"
				@click.stop="close"
			></div>
			<div
				v-else-if="isOpen"
				class="fixed top-safe right-safe bottom-safe left-safe"
				:style="{ zIndex: zIndex || 4 }"
				@click.stop="close"
			></div>
			<div
				ref="floating"
				:style="[floatingStyles, extraFloatingStyles]"
				:class="['pointer-events-none z-[99] text-start', shouldCenterAsDialog ? 'p-4' : '']"
				@click.stop
			>
				<div class="pointer-events-auto flex flex-col">
					<slot name="menu" v-bind="{ isOpen, open, close }"></slot>
				</div>
			</div>
		</Teleport>
		<template v-else>
			<div
				ref="floating"
				:style="[floatingStyles, extraFloatingStyles]"
				class="pointer-events-none z-[99] text-start"
				@click.stop
			>
				<div class="pointer-events-auto flex flex-col">
					<slot name="menu" v-bind="{ isOpen, open, close }"></slot>
				</div>
			</div>
		</template>
	</div>
</template>

<script setup lang="ts">
import {
	autoUpdate,
	flip,
	offset as fuOffset,
	shift,
	size,
	useFloating,
	type UseFloatingOptions
} from "@floating-ui/vue"
import type { StyleValue } from "vue"

const app = useNuxtApp()

const emit = defineEmits<{
	opened: []
	closed: []
}>()

const props = withDefaults(
	defineProps<{
		placement?: UseFloatingOptions["placement"]
		strategy?: UseFloatingOptions["strategy"]
		offset?: number
		body?: boolean
		referenceClass?: unknown
		buttonContainerClass?: unknown
		zIndex?: number
	}>(),
	{
		placement: "left-start",
		strategy: "fixed",
		offset: 6,
		body: true,
		zIndex: 4
	}
)

const buttonEl = shallowRef<HTMLElement>()
const reference = shallowRef<HTMLElement>()
const floating = shallowRef<HTMLElement>()

const isOpen = ref(false)
const shouldCenterAsDialog = ref(false)

const isRTL = computed(() => ["fa"].includes(app.$i18n.locale.value))

const dirPlacement = computed(() => {
	let p = props.placement
	if (!isRTL.value) return p
	else if (p == "left") p = "right"
	else if (p == "right") p = "left"
	else if (p == "left-end") p = "right-end"
	else if (p == "right-end") p = "left-end"
	else if (p == "left-start") p = "right-start"
	else if (p == "right-start") p = "right-end"
	return p
})

const open = () => {
	isOpen.value = true
	emit("opened")
}

const close = () => {
	isOpen.value = false
	emit("closed")
}

const { floatingStyles, middlewareData } = useFloating(reference, floating, {
	placement: dirPlacement.value,
	strategy: props.strategy,
	whileElementsMounted: autoUpdate,
	middleware: [
		fuOffset(props.offset),
		shift({ padding: 8 }),
		flip({
			fallbackPlacements: [
				"top",
				"bottom",
				"left",
				"right",
				"top-start",
				"top-end",
				"bottom-start",
				"bottom-end",
				"left-start",
				"left-end",
				"right-start",
				"right-end"
			]
		}),
		size({
			padding: 8,
			apply({ availableWidth, availableHeight, rects }) {
				const viewportWidth = window.innerWidth
				const viewportHeight = window.innerHeight
				const floatingWidth = rects.floating.width
				const floatingHeight = rects.floating.height

				// Check if popover is too large for viewport
				const widthRatio = floatingWidth / viewportWidth
				const heightRatio = floatingHeight / viewportHeight
				const isTooLarge = widthRatio > 0.95 || heightRatio > 0.9

				// Check if even after flip/shift there's still not enough space
				const hasInsufficientSpace =
					availableWidth < floatingWidth - 16 || availableHeight < floatingHeight - 16

				// Only trigger dialog mode if truly no good placement exists
				shouldCenterAsDialog.value = isTooLarge || hasInsufficientSpace
			}
		})
	]
})

const extraFloatingStyles = computed<StyleValue>(() => {
	if (shouldCenterAsDialog.value) {
		return {
			position: "fixed",
			top: "50%",
			left: "50%",
			transform: "translate(-50%, -50%)",
			maxWidth: "calc(100vw - 64px)",
			maxHeight: "calc(100vh - 64px)"
		}
	}
	return {}
})

const referenceStyles = computed<StyleValue>(() => {
	let p = props.placement
	if (middlewareData.value.flip?.index) {
		if (p == "left") p = "right"
		else if (p == "bottom") p = "top"
		else if (p == "bottom-start") p = "top-start"
		else if (p == "bottom-end") p = "top-end"
		else if (p == "top") p = "bottom"
		else if (p == "top-start") p = "bottom-start"
		else if (p == "top-end") p = "bottom-end"
	}
	if (p == "left") {
		return { insetInlineStart: 0, top: 0, bottom: 0, marginTop: "auto", marginBottom: "auto" }
	} else if (p == "left-start") {
		return { insetInlineStart: "-1px", top: 0 }
	} else if (p == "left-end") {
		return { insetInlineStart: "-1px", bottom: 0 }
	} else if (p == "top") {
		return { top: 0, left: 0, right: 0, marginLeft: "auto", marginRight: "auto" }
	} else if (p == "top-start") {
		return { insetInlineStart: "-1px", top: 0 }
	} else if (p == "top-end") {
		return { insetInlineEnd: "-1px", top: 0 }
	} else if (p == "right") {
		return { insetInlineEnd: 0, top: 0, bottom: 0, marginTop: "auto", marginBottom: "auto" }
	} else if (p == "right-start") {
		return { insetInlineEnd: "-1px", top: 0 }
	} else if (p == "right-end") {
		return { insetInlineEnd: "-1px", bottom: 0 }
	} else if (p == "bottom") {
		return { bottom: 0, left: 0, right: 0, marginLeft: "auto", marginRight: "auto" }
	} else if (p == "bottom-start") {
		return { insetInlineStart: "-1px", bottom: 0 }
	} else if (p == "bottom-end") {
		return { insetInlineEnd: "-1px", bottom: 0 }
	} else {
		return { insetInlineStart: "-1px", top: 0 }
	}
})

defineExpose({
	isOpen,
	open,
	close
})
</script>
